import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Building2, 
  Plus, 
  Search,
  Edit,
  Trash2,
  X,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { franquiaService } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const Franquias = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFranquia, setEditingFranquia] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    host: '',
    status: 'A',
    data_inativacao: ''
  });
  const queryClient = useQueryClient();

  // Buscar franquias
  const { data: franquias, isLoading, error } = useQuery(
    'franquias',
    () => franquiaService.getAll(true),
    {
      refetchOnWindowFocus: false
    }
  );

  // Mutation para criar franquia
  const createMutation = useMutation(franquiaService.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('franquias');
      setShowModal(false);
      setFormData({ nome: '', cnpj: '', host: '', status: 'A', data_inativacao: '' });
      toast.success('Franquia criada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar franquia');
    }
  });

  // Mutation para atualizar franquia
  const updateMutation = useMutation(
    ({ id, data }) => franquiaService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('franquias');
        setShowModal(false);
        setEditingFranquia(null);
        setFormData({ nome: '', cnpj: '', host: '', status: 'A', data_inativacao: '' });
        toast.success('Franquia atualizada com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar franquia');
      }
    }
  );

  // Mutation para deletar franquia
  const deleteMutation = useMutation(franquiaService.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('franquias');
      toast.success('Franquia excluída com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir franquia');
    }
  });

  // Mutation para alternar status
  const toggleStatusMutation = useMutation(
    (id) => franquiaService.toggleStatus(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('franquias');
        toast.success('Status alterado com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao alterar status');
      }
    }
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'A': return 'bg-green-100 text-green-800';
      case 'I': return 'bg-yellow-100 text-yellow-800';
      case 'B': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'A': return 'Ativo';
      case 'I': return 'Inativo';
      case 'B': return 'Bloqueado';
      default: return 'Desconhecido';
    }
  };

  const franquiasData = Array.isArray(franquias?.data) ? franquias.data : [];
  const searchLower = searchTerm.toLowerCase();
  const filteredFranquias = franquiasData
    .filter(franquia =>
      (franquia.nome || '').toLowerCase().includes(searchLower) ||
      (franquia.cnpj || '').includes(searchTerm)
    )
    .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cnpj) {
      toast.error('Nome e CNPJ são obrigatórios');
      return;
    }

    if (editingFranquia) {
      updateMutation.mutate({ id: editingFranquia.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (franquia) => {
    setEditingFranquia(franquia);
    // Formatar data_inativacao para o input date (YYYY-MM-DD)
    let dataInativacao = '';
    if (franquia.data_inativacao) {
      const date = new Date(franquia.data_inativacao);
      dataInativacao = date.toISOString().split('T')[0];
    }
    setFormData({
      nome: franquia.nome,
      cnpj: franquia.cnpj,
      host: franquia.host || '',
      status: franquia.status,
      data_inativacao: dataInativacao
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta franquia?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStatus = (id) => {
    toggleStatusMutation.mutate(id);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFranquia(null);
    setFormData({ nome: '', cnpj: '', host: '', status: 'A', data_inativacao: '' });
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Erro ao carregar franquias: {error.message}</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Franquias</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerenciar as franquias do sistema
          </p>
        </div>
        
        <button
          onClick={() => setShowModal(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Franquia
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building2 className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total de Franquias
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {franquiasData.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Ativas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {franquiasData.filter(f => f.status === 'A').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Inativas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {franquiasData.filter(f => f.status === 'I').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Bloqueadas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {franquiasData.filter(f => f.status === 'B').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Buscar por nome ou CNPJ..."
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Lista de Franquias
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {filteredFranquias.length} franquia(s) encontrada(s)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CNPJ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Host
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Cadastro
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFranquias.map((franquia) => (
                <tr key={franquia.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {franquia.nome}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {franquia.cnpj}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {franquia.host || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(franquia.status)}`}>
                      {getStatusText(franquia.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(franquia.datahora_cadastro).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleToggleStatus(franquia.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Alternar Status"
                      >
                        {franquia.status === 'A' ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      </button>
                      <button 
                        onClick={() => handleEdit(franquia)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(franquia.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingFranquia ? 'Editar Franquia' : 'Nova Franquia'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    CNPJ *
                  </label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="00.000.000/0000-00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Host
                  </label>
                  <input
                    type="text"
                    value={formData.host}
                    onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="exemplo.com (https:// será adicionado automaticamente)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      setFormData({ 
                        ...formData, 
                        status: newStatus,
                        // Se mudar para Inativo e não tiver data, usar data atual
                        data_inativacao: newStatus === 'I' && !formData.data_inativacao 
                          ? new Date().toISOString().split('T')[0] 
                          : (newStatus === 'A' ? '' : formData.data_inativacao)
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="A">Ativo</option>
                    <option value="I">Inativo</option>
                    <option value="B">Bloqueado</option>
                  </select>
                </div>

                {/* Campo de Data de Inativação - visível apenas quando status é Inativo */}
                {formData.status === 'I' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Data de Inativação
                    </label>
                    <input
                      type="date"
                      value={formData.data_inativacao}
                      onChange={(e) => setFormData({ ...formData, data_inativacao: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      A franquia não será contabilizada nos períodos a partir desta data.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {createMutation.isLoading || updateMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Franquias;
